// src/store/historySlice.js

import i18n from '../locales/i18n';

const createHistorySlice = (set, get, ref) => {

  const { t } = i18n;

  ref.history = {
    userStrokes: {},
    imageCache: new Map(),
    layerImageCache: {},

    save: {
      threshold: 1000,
      sendTimer: null,
      lastCallTime: 0,
    }
  };

  // Функция для проверки возможности отмены штриха (если штрих слишком старый или не последний)

  function ifCanUndoBoard (myStroke) {

    if (!ref.info.game.board) { return true; }

    const allStrokes = Object.values(ref.history.userStrokes)
    .flat()
    .filter(stroke => !stroke.cancelled)
    .sort((a, b) => a.time - b.time);

    if (myStroke.time > ref.info.methods.serverTimeFun() - 60 * 1000) { return true; }

    const lastStrokeIsMine = myStroke && myStroke?.userId === allStrokes[allStrokes.length - 1]?.userId;

    if (lastStrokeIsMine) { return true; }

    const popupParams = {
      type: 'destructive',
      title: t('cant_undo_old.title'),
      message: t('cant_undo_old.text'),
      buttons: [
        { id: 'ok', type: 'submit', text: t('cant_undo_old.button_ok') },
      ]
    };
    ref.menu.methods.showPopupMessage(popupParams);

    return false;;
    
  }

  function countRenderDifficulty (lastStroke) {

    const needToRender = prepareStrokesForCount(lastStroke, lastStroke.layerId);

    const strokeTypes = {
      special: 0,
      fill: 0,
    };

    for (let stroke of needToRender) {
      strokeTypes[stroke.type] = strokeTypes[stroke.type] || 0;
      strokeTypes[stroke.type] ++;

      if(['feather', 'ink', 'oil', 'pencil', 'blur', 'noise', 'watercolor', 'bristle', 'rembrandt', 'glyph','test'].includes(stroke.brush)
      || stroke.effect
      ) { strokeTypes.special ++; }
    }

    const difficulty = needToRender.length / 500 + strokeTypes.fill / 2 + strokeTypes.special / 10;

    return difficulty;
    
  }

  function prepareStrokesForCount (lastStroke, layerId) {

    const allStrokes = Object.values(ref.history.userStrokes).flat();
    const combinedStrokes = allStrokes.filter(stroke => {
      return !stroke.cancelled 
      && stroke.layerId === layerId
      && stroke.time !== lastStroke.time;
    }).sort((a, b) => a.time - b.time);

    const lastClearIndex = combinedStrokes.map(stroke => stroke.type).lastIndexOf('clear');
    const strokesToRender = combinedStrokes.slice(lastClearIndex + 1);

    const layerCache = ref.history.layerImageCache[layerId] || new Map();

    const lastCacheIndex = strokesToRender.map(stroke => {
      const cachedData = layerCache.get(stroke.time);
      if (cachedData) { return true } else { return false; }
    }).lastIndexOf(true);

    const needToRender = strokesToRender.slice(Math.max(lastCacheIndex, 0));
    return needToRender;

  }


  function undoTransform (stroke) {

    if (stroke.type === 'transformations') {
      // Новый формат - множественные слои
      Object.keys(stroke.prev).forEach(layerId => {
        const targetLayer = ref.convy.methods.getLayer(layerId);
        const order = targetLayer.info.order;
        ref.convy.layerInfo[layerId] = JSON.parse(JSON.stringify(stroke.prev[layerId]));
        targetLayer.info = ref.convy.layerInfo[layerId];
        targetLayer.info.order = order;
        targetLayer.ready = false;
      });
      
      ref.menu.methods.showTemporaryHint(t('tooltip.undo', {layerName: `${Object.keys(stroke.prev).length} слоев`}), { force: true, duration: 1000 });
    }

  }
  
  function redoTransform (stroke) {
    if (stroke.type === 'transformations') {
      // Новый формат - множественные слои
      Object.keys(stroke.info).forEach(layerId => {
        const targetLayer = ref.convy.methods.getLayer(layerId);
        const order = targetLayer.info.order;
        ref.convy.layerInfo[layerId] = JSON.parse(JSON.stringify(stroke.info[layerId]));
        targetLayer.info = ref.convy.layerInfo[layerId];
        targetLayer.info.order = order;
        targetLayer.ready = false;
      });
      
      ref.menu.methods.showTemporaryHint(t('tooltip.redo', {layerName: `${Object.keys(stroke.info).length} слоев`}), { force: true, duration: 1000 });
    }
  }

  function getStrokesToSend(strokes, limit = 100) {

    if (ref.connection.lackOfStrokes) {
      limit = limit + ref.connection.lackOfStrokes + 100;
    }

    let visibleCount = 0;
    
    for (let i = strokes.length - 1; i >= 0; i--) {
      const stroke = strokes[i];
      if (!stroke.cancelled && !stroke.hidden) {
        visibleCount++;
      }
      if (visibleCount >= limit) {
        return strokes.slice(i);
      }
    }
    return strokes;
  }




  ref.history.methods = {
    // Отмена последнего действия
    undoLastAction: (parm = {}) => {

      const activeUserId = ref.telegram.activeUserId;
      let myStrokes = ref.history.userStrokes[activeUserId];

      if (!myStrokes) return;

      ref.menu.methods.appleHaptic('medium');

      const actualHistory = myStrokes
        .filter((stroke) => !stroke.cancelled && !stroke.tech)
        .sort((a, b) => a.time - b.time);

      const lastStroke = actualHistory.pop();
      const prevStroke = actualHistory.pop();

      if (!lastStroke) {
        return ref.menu.methods.showTemporaryHint(t('tooltip.no_more_undo'), { force: true });
      }

      if (!ifCanUndoBoard(lastStroke)) return;
      if (countRenderDifficulty(lastStroke) > 10) {
        return ref.history.methods.confirmUndoReload(lastStroke);
      }

      let layer;
      if (lastStroke.layerId) {
        layer = ref.convy.methods.getLayer(lastStroke.layerId);
        layer.ready = false;
        if (!layer.info.visible) {
          return ref.menu.methods.showTemporaryHint(t('tooltip.layer_is_invisible'), { force: true });
        }
      }
     

      lastStroke.cancelled = ref.info.methods.serverTimeFun();
      lastStroke.time++;
      delete lastStroke.rendered;

      if (prevStroke) {
        if (prevStroke.hidden) {
          prevStroke.time++;
          delete prevStroke.hidden;
          delete prevStroke.rendered;
        }
      }

      undoTransform (lastStroke);

      ref.history.methods.saveHistory();
      ref.menu.methods.showTemporaryHint(t('tooltip.undo', {layerName: layer?.info.name || ''}), { force: true, duration: 1000 });
      ref.drawing.methods.redraw();

    },

    // Восстановление последнего отменённого действия
    redoLastAction: () => {
      const activeUserId = ref.telegram.activeUserId;
      let myStrokes = ref.history.userStrokes[activeUserId];
      if (!myStrokes) return;

      ref.menu.methods.appleHaptic('medium');

      const cancelledHistory = myStrokes
        .filter((stroke) => stroke.cancelled)
        .sort((a, b) => b.cancelled - a.cancelled);
      const firstStroke = cancelledHistory.shift();
      const actualHistory = myStrokes.filter((stroke) => !stroke.cancelled);
      const lastStroke = actualHistory[actualHistory.length - 1];

      if (firstStroke) {
        let layer;
        if (firstStroke.layerId) {
          layer = ref.convy.methods.getLayer(firstStroke.layerId);
          if (!layer.info.visible) {
            return ref.menu.methods.showTemporaryHint(t('tooltip.layer_is_invisible'), { force: true });
          }
          layer.ready = false;
        }

        delete firstStroke.cancelled;
        firstStroke.time++;
        delete firstStroke.rendered;
        if (lastStroke && lastStroke.shapeId && lastStroke.shapeId === firstStroke.shapeId) {
          lastStroke.hidden = true;
          lastStroke.time++;
          delete lastStroke.rendered;
        }

        redoTransform (firstStroke)

        ref.history.methods.saveHistory();
        ref.menu.methods.showTemporaryHint(t('tooltip.redo', {layerName: layer?.info?.name || ''}), { force: true, duration: 1000 });
        ref.drawing.methods.redraw();

      } else {
        ref.menu.methods.showTemporaryHint(t('tooltip.no_more_redo'), { force: true });
      }
    },


    handleStrokeHistoryClick: (stroke) => {

      if (!ifCanUndoBoard(stroke)) return;
      ref.history.loadTime = Math.min(ref.history.loadTime, stroke.time);
    
      if (stroke.cancelled) {
        // Восстановление штриха
        delete stroke.cancelled;
        stroke.time++; // Увеличиваем время для уникальности
        delete stroke.rendered;

        redoTransform (stroke)
  
        let layer;
        if (stroke.layerId) {
          layer = ref.convy.methods.getLayer(stroke.layerId);
          layer.ready = false;
        }
  
        ref.history.methods.saveHistory();
        ref.drawing.methods.redraw().then(()=>{
          ref.menu.methods.showTemporaryHint(t('tooltip.redo', {layerName: layer?.info?.name || ''}), { force: true, duration: 1000 });
        });
      } else {
        // Отмена штриха
        if (!ifCanUndoBoard(stroke)) return;
  
        stroke.cancelled = ref.info.methods.serverTimeFun();
        stroke.time++;
        delete stroke.rendered;
        
        undoTransform (stroke)

        let layer;
        if (stroke.layerId) {
          layer = ref.convy.methods.getLayer(stroke.layerId);
          layer.ready = false;
        }
  
        ref.history.methods.saveHistory();
        ref.drawing.methods.redraw().then(()=>{
          ref.menu.methods.showTemporaryHint(t('tooltip.undo', {layerName: layer?.info?.name || ''}), { force: true, duration: 1000 });
        });
      }
  
      ref.menu.methods.appleHaptic('medium'); // Тактильная отдача
    },


    // Подтверждение отмены с перезагрузкой (для сложных/старых штрихов)
    confirmUndoReload: (lastStroke) => {
      if (ref.drawing.isRendering) return;

      ref.menu.methods.showPopupMessage({
        type: 'destructive',
        title: t('undo_reload.title'),
        message: t('undo_reload.text'),
        buttons: [
          { id: 'cancel', type: 'cancel', text: t('undo_reload.button_cancel') },
          { id: 'confirm', type: 'destructive', text: t('undo_reload.button_confirm') },
        ],
        callback: (buttonId) => {
          if (buttonId === 'confirm') {
            lastStroke.time--;
            lastStroke.cancelled = ref.info.methods.serverTimeFun();

            ref.history.methods.saveHistory();
            // ref.drawing.methods.redraw();

            setTimeout(() => { 
              window.location.reload(); 
            }, 200);
          }
        },
      });

    },

    // Подтверждение очистки доски с показом popup (в данном примере без выбора цвета)
    confirmClearCanvas: () => {

      if (ref.drawing.isRendering) return;

      if (ref.info.game.board) {

        const activeLayer = ref.convy.methods.getActiveLayer();
        const someoneElsesStroke = Object.values(ref.history.userStrokes)
        .flat()
        .filter(stroke=>stroke.layerId === activeLayer.id && !stroke.canceled && stroke.userId !== ref.telegram.activeUserId)[0];

        if (someoneElsesStroke) {
          return ref.menu.methods.showPopupMessage({
            title: t('cant_clear_board.title'),
            message: t('cant_clear_board.text'),
            buttons: [
              { id: 'ok', type: 'submit', text: t('cant_clear_board.button_ok') },
            ]
          });
        }
      }


      const ClearCanvasContent = ({ text, colors, onColorClick }) => {
        return (
          <>
            <span 
              className="message-popup-text"
              dangerouslySetInnerHTML={{ __html: text || '' }}
            />
            <div style={{ 
              display: 'flex', 
              gap: '13px',
              marginBottom: '10px', 
              marginRight: '10px', 
              justifyContent: 'flex-end',
              marginLeft: 'auto', //
              flexWrap: 'wrap',          // Добавляем перенос элементов
              }}>
              {colors.map((color, index) => (
                <div
                  key={index}
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    cursor: 'pointer',
                    boxShadow: '0 0 0 0.5px grey',
                  }}
                  onClick={() => onColorClick(color)}
                />
              ))}
            </div>
          </>
        );
      };
  
      const colorsArr = [
        ...ref.brush.lastColors,
        ref.brush.brushColor,
      ];
      const uniqueColorsArr = ['#FFFFFFFF', '#000000FF', ...new Set(colorsArr.slice(-10))];
  
      const popupParams = {
        title: t('clear.title'),
        // message: t('clear.text'),
        content: <ClearCanvasContent 
          text={t('clear.text')} 
          colors={uniqueColorsArr} 
          onColorClick={(color)=>{
            ref.history.methods.clearCanvas(color);
            ref.menu.methods.showPopupMessage(null);
          }} 
        />,
        buttons: [
          { id: 'cancel', type: 'cancel', text: t('clear.button_cancel') },
          { id: 'clear', type: 'destructive', text: t('clear.button_confirm') },
        ]
      };
  
      popupParams.callback = (buttonId, color) => {
        if (buttonId === 'clear') { ref.history.methods.clearCanvas(color); }
      }
      ref.menu.methods.showPopupMessage(popupParams)

    },

    clearCanvas: (color) => {

      const layer = ref.convy.methods.getActiveLayer();
      const activeUserId = ref.telegram.activeUserId;

      let myStrokes = ref.history.userStrokes[activeUserId] || [];

      if (color) {
        const backgroundStroke = {
          type: 'background',
          color,
          time: ref.info.methods.serverTimeFun(),
          userId: ref.telegram.activeUserId,
          layerId: layer.id,
        };
        myStrokes.push(backgroundStroke);
      } else {
        const clearStroke = {
          type: 'clear',
          time: ref.info.methods.serverTimeFun(),
          userId: ref.telegram.activeUserId,
          layerId: layer.id,
        };
        myStrokes.push(clearStroke);
      }

      ref.history.userStrokes[activeUserId] = myStrokes.filter(stroke => !stroke.cancelled);
      layer.ready = false;

      ref.menu.methods.appleHaptic('light');
      ref.drawing.methods.redraw();
      ref.history.methods.saveHistory();

    },

    // Групповая перерисовка: отправка события через сокет
    groupRedraw: () => {
      const socket = get().socket;
      if (!socket) return;
      socket.emit(
        'redraw',
        {
          gameId: ref.telegram.gameId,
          gameParam: ref.telegram.gameParam,
          userId: ref.telegram.activeUserId,
        },
        (result) => {
          if (window.croco && window.croco.haptic) window.croco.haptic();
        }
      );
    },

    newTransformationStroke: (stroke = {}) => {

      // В режиме Мафии переключения видов происходят локально.
      // Нам не нужно сохранять изменения прозрачности/позиции в общую историю.
      if (ref.info.game?.mode === 'mafia') {
        return;
      }

      let { layerIds, } = stroke;
      
      const newStroke = {
        time: ref.info.methods.serverTimeFun(),
        userId: ref.telegram.activeUserId,
        type: 'transformations',
        ...stroke,
      };

      const userStrokes = ref.history.userStrokes;
      const activeUserId = ref.telegram.activeUserId;
      let myStrokes = userStrokes[activeUserId];
      myStrokes.push(newStroke);
      userStrokes[activeUserId] = myStrokes.filter(stroke => !stroke.cancelled);

      ref.history.methods.saveHistory();

      // Отмечаем все затронутые слои как трансформированные
      layerIds.forEach(id => {
        const layer = ref.convy.methods.getLayer(id);
        layer.info.wasTransformed = true;
        layer.ready = false;
      });

    },

    newBackgroundChangeStroke: (stroke) => {

      const newStroke = {
        time: ref.info.methods.serverTimeFun(),
        userId: ref.telegram.activeUserId,
        type: 'baseBackground',
        ...stroke,
      };

      const userStrokes = ref.history.userStrokes;
      const activeUserId = ref.telegram.activeUserId;
      let myStrokes = userStrokes[activeUserId] || [];
      myStrokes.push(newStroke);

      ref.history.methods.saveHistory();
    },

    layersInfoChangeStroke: ({ prev, info }) =>{

      if (ref.info.game?.mode === 'mafia') {
        return;
      }

      if (!prev) { return; }
      info =  info || JSON.parse(JSON.stringify(ref.convy.layerInfo));

      const newStroke = {
        time: ref.info.methods.serverTimeFun(),
        userId: ref.telegram.activeUserId,
        type: 'layers',
        prev,
        info,
        tech: true,
      }

      const userStrokes = ref.history.userStrokes;
      const activeUserId = ref.telegram.activeUserId;
      let myStrokes = userStrokes[activeUserId];
      myStrokes.push(newStroke);
      userStrokes[activeUserId] = myStrokes.filter(stroke => !stroke.cancelled);

      ref.history.methods.saveHistory();
      ref.work.methods.saveGameSets({ layerInfo: ref.convy.layerInfo });

    },


    // Сохранение истории: вызывает функцию сохранения (например, отправку на сервер)
    saveHistory: async (parm = {}) => {

      const { history, telegram } = ref;
      const { save } = history;

      const { attempt = 1 } = parm;
      if (attempt > 40) { return; }
      if (attempt > 3) { 
        await new Promise(resolve=>{ 
          save.attemptTimeout = setTimeout(resolve, 3 * 1000) 
        });  
      }
      
      clearTimeout(save.attemptTimeout);

      const activeUserId = telegram.activeUserId;
      const myHistory = history.userStrokes[activeUserId] || [];
      const historyToSave = [...myHistory];

      ref.history.methods.saveStrokes?.({
        strokes: historyToSave,
        ...parm,
      });
    },

    saveStrokes: async ({
      strokes,
      forced,
      attempt = 1,
    }) => {

      const { socket, connectSocket } = get();
      if (!socket) {return;}
      if (!socket.connected) { 
        ref.history.methods.handleSaveStrokesError(`❌🌐 strokes save error: socket not connected`, attempt);
        return connectSocket()
       }

      const { telegram, info, brush, convy, connection, history } = ref;
      const { save } = history;
      const { user } = info;

      if (ref.telegram.testCanvas) { return; }

      window.Telegram.WebApp.enableClosingConfirmation();
    
      const {
        lastColors: colors,
      } = brush;
    
      const dimensions = convy.main.dimensions;
    
      clearTimeout(save.sendTimer);
      const needToWait = save.lastCallTime + save.threshold - Date.now();
    
      if (!forced && needToWait > 0) { 
        await new Promise(resolve=>{save.sendTimer = setTimeout(resolve, needToWait)}); 
      }
      save.lastCallTime = Date.now();
    
      try {
    
        const initTime = Date.now();
        const strokesToSend = getStrokesToSend(strokes);
    
        const {
          gameId, 
          telegramUserId,
          activeUserId,
          clientId,
          moderId,
          gameParam,
        } = telegram;

        window.croco?.log('⬅️💾emit strokes');
    
        socket.emit('strokes', { 
          gameId, 
          gameParam, 
          userId: telegramUserId,
          clientId,
          debugMode: user.debugMode,
         }, { 
          dimensions, 
          strokes: strokesToSend, 
          length: strokes.length, 
          userId: activeUserId, 
          moderId,
          colors,
          }, (result)=>{
            window.croco?.log(`✔️💾strokes save result: ${JSON.stringify(result)}`);

            if (result) { 
              if (result?.lackOfStrokes > 0) {

                connection.lackOfStrokes = result.lackOfStrokes;
                ref.history.methods.handleSaveStrokesError(`➖❗️lack of strokes: ${result?.lackOfStrokes}`, attempt);

              } else {
                delete connection.lackOfStrokes;
                connection.lastSaveTime = Date.now();
                window.Telegram.WebApp.disableClosingConfirmation();

                // Уведомляем мафию о том, что игрок нарисовал
                if (ref.mafia && ref.mafia.methods) {
                  ref.mafia.methods.setHasDrawn(true);
                }
              }
          
            }
        });
    
        setTimeout(() => {
          if (initTime > connection.lastSaveTime) {
            ref.history.methods.handleSaveStrokesError(`⌛️❗️saving strokes timeout`, attempt);
          }
        }, 3000);
    
        console.log("Sending strokes: ", strokes.length); // Для отладки
    
      } catch (e) {
    
        ref.history.methods.handleSaveStrokesError(`❌💾strokes save error: ${e?.message}`, attempt, e);

      }
    },

    handleSaveStrokesError: (text, attempt, error) => {

      window.croco?.log(text + ` | #${attempt}`);
      ref.connection.lastConnectionErrorTime = Date.now();
      window.Telegram.WebApp.enableClosingConfirmation();

      ref.history.methods.saveHistory({ attempt: attempt + 1 });

      if (error) { console.error(error); }

    },

    // Полная очистка истории (для смены раунда)
    clearHistory: () => {
      // 1. Сбрасываем хранилище штрихов
      for (let userId in ref.history.userStrokes) {
        ref.history.userStrokes[userId] = [];
      }
      const layers = ref.convy?.layers || {};
      for (let layerId in layers) {
        layers[layerId].ready = false;
      }
    }
    
  };

  return {
    userStrokes: {},
  };
};

export default createHistorySlice;
